import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertTitle,
  Button,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { zTagType } from '@strength/domain';
import { getTMessageForEnum } from '@strength/i18n';
import { useCallback, useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { trpc } from '../../../../trpc/react';
import { Drawer, DrawerFooter, DrawerHeader } from '../../../components/drawer';
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
} from '../../../components/form/form';
import { cn } from '../../../components/utils';
import { useViewer } from '../../../context/viewer';
import { Tag } from './types';
import { libraryTagTypes } from './utils';

const TagFormSchema = z.object({
  name: z.string().min(3, 'nameMinLength'),
  type: zTagType,
});
export type TagForm = z.infer<typeof TagFormSchema>;

export const TagDrawer = ({
  tag,
  open,
  onClose,
  onSubmit,
  onDelete,
}: {
  tag?: Tag;
  open: boolean;
  onClose: () => void;
  onSubmit: (tagForm: TagForm, tagId?: string) => void;
  onDelete: (tagId: string) => void;
}) => {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  const isEdit = !!tag;
  const disableEdit = isEdit && !hasPermissions('library.tags', 'edit');
  const disableCreate = !isEdit && !hasPermissions('library.tags', 'create');
  const disableWrite = isEdit ? disableEdit : disableCreate;
  const disableDelete = !hasPermissions('library.tags', 'delete');
  const title = isEdit ? t('editTag') : t('createTag');

  const form = useForm<TagForm>({
    resolver: zodResolver(TagFormSchema),
    defaultValues: getTagForm(tag),
  });
  const { handleSubmit, control } = form;

  const onSubmitHandler = (form: TagForm) => {
    onSubmit(form, tag?.id);
    onClose();
  };

  const canBeDeletedQuery = trpc.tag.canBeDeleted.useQuery(
    {
      id: tag?.id ?? '',
    },
    {
      enabled: !!tag?.id,
    },
  );

  const onDeleteHandler = useCallback(() => {
    tag?.id &&
      !canBeDeletedQuery.isLoading &&
      canBeDeletedQuery.data === null &&
      onDelete(tag.id);
    onClose();
  }, [tag, onDelete, onClose, canBeDeletedQuery]);

  const [name, type] = form.watch(['name', 'type']);

  const [debouncedName, setDebouncedName] = useState(name);
  const [debouncedType, setDebouncedType] = useState(type);

  // Debounce the name and type field
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedName(name);
      setDebouncedType(type);
    }, 600); // 600ms debounce delay

    return () => clearTimeout(timer);
  }, [name, type]);

  const hasTagWithNameAndTypeQuery = trpc.tag.hasTagWithNameAndType.useQuery(
    {
      name: debouncedName,
      excludeId: isEdit ? tag?.id : undefined,
      type: debouncedType,
    },
    {
      enabled: !!debouncedName && !!debouncedType,
    },
  );

  const isTagDuplicate = hasTagWithNameAndTypeQuery.data ?? false;
  const deleteTooltip = canBeDeletedQuery.data
    ? t(...canBeDeletedQuery.data)
    : disableDelete
      ? t('permissionDisabled.delete', { item: t('tags') })
      : '';
  // The permission reason is also associated programmatically (not just via
  // the hover tooltip) so it is announced for the disabled control.
  const deleteReasonId = useId();

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <DrawerHeader onClose={onClose}>
        <Typography variant="h2">{title}</Typography>
      </DrawerHeader>
      <Form {...form}>
        <form
          id="tag-form"
          onSubmit={handleSubmit(onSubmitHandler)}
          className="flex flex-col gap-2 flex-grow"
        >
          <div className="flex flex-col gap-2 grow p-6 pt-0">
            {isTagDuplicate && (
              <Alert severity="error">
                <AlertTitle>
                  {t('tagNameAndTypeMustBeUnique', {
                    name: debouncedName,
                    type: t(...getTMessageForEnum(debouncedType)),
                  })}
                </AlertTitle>
              </Alert>
            )}
            <FormField
              control={control}
              name="type"
              disabled={disableWrite}
              render={({ field }) => (
                <FormItem>
                  <TextField label={t('type')} select {...field} fullWidth>
                    {libraryTagTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {t(...getTMessageForEnum(type))}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="name"
              disabled={disableWrite}
              render={({ field }) => (
                <FormItem>
                  <TextField
                    label={t('name')}
                    {...field}
                    placeholder={t('enterName')}
                    fullWidth
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>
      <DrawerFooter
        className={cn(
          isEdit ? 'justify-between' : 'justify-end',
          'flex flex-row gap-2 w-full p-8',
        )}
      >
        {isEdit && (
          <Tooltip title={<div>{deleteTooltip}</div>} arrow>
            <span>
              <Button
                variant="containedDestructive"
                onClick={onDeleteHandler}
                aria-describedby={disableDelete ? deleteReasonId : undefined}
                disabled={
                  canBeDeletedQuery.isLoading ||
                  deleteTooltip !== '' ||
                  disableDelete
                }
              >
                {t('delete')}
              </Button>
              {disableDelete && (
                <span id={deleteReasonId} className="sr-only">
                  {deleteTooltip}
                </span>
              )}
            </span>
          </Tooltip>
        )}
        <div className="flex flex-row gap-2">
          <Button variant="outlined" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            form="tag-form"
            disabled={
              !form.formState.isValid ||
              isTagDuplicate ||
              hasTagWithNameAndTypeQuery.isLoading ||
              disableWrite
            }
          >
            {t('save')}
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

const getTagForm = (tag?: Tag): TagForm => {
  if (!tag) {
    return {
      name: '',
      type: 'exercise',
    };
  }
  return {
    ...tag,
  };
};
